import angular from 'angular';
import constants from '../constants';
import appCache from './ApplicationCache';
import { createError } from '../error';
import env from '../env';

const requestStatsCallbacks = [];

/**
 * A 2xx of markup is never a valid payload (a request bounced to /login.php resolves as 200 HTML).
 * Any leading tag counts — _404.tpl starts with <link>; a string body means $http saw no JSON.
 */
function isHtmlDocumentResponse(response) {
  if (!response || typeof response.data !== 'string') {
    return false;
  }
  return /^[\uFEFF\s]*</.test(response.data);
}

export class APIService {
  constructor(
    $http,
    $location,
    $q,
    $timeout,
    $log,
    API_URL,
    CACHE_ENABLED,
    ApplicationCache,
    Base64,
    LogService,
    $window,
    $rootScope
  ) {
    "ngInject";
    this.$http = $http;
    this.$location = $location;
    this.$q = $q;
    this.$timeout = $timeout;
    this.$log = $log;
    this.API_URL = API_URL;
    this.CACHE_ENABLED = CACHE_ENABLED;
    this._fullAPIURL = this._ensureFullURL(API_URL);
    this._fullAPIURLWithNoPath = this._fullAPIURL.replace(/(^.*\/\/[^\/]+\/).*$/, '$1');

    this.ApplicationCache = ApplicationCache;
    this.Base64 = Base64;
    this.LogService = LogService;
    this.$window = $window;
    this.$rootScope = $rootScope;
    this._cacheEnabled = this.CACHE_ENABLED;
    this._onlineStatusListeners = [];
    this.isOnline = navigator.onLine;
    this.setOnlineStatusWatcher();

    this._slowRequestCallbacks = [];
    this._haveCredentials = false;
    this._haveApplicationCredentials = false;
    this._authToken = null;
  }

  _ensureFullURL(pathOrURL) {
    if (!pathOrURL.match(/:\/\//)) {
      let protocol = this.$location.protocol();
      let host = this.$location.host();
      let port = this.$location.port();
      if ((protocol === 'http' && port === 80) || (protocol === 'https' && port === 443)) {
        port = '';
      } else {
        port = `:${port}`;
      }
      let path = this.$location.path();
      if (pathOrURL.match(/^\//)) {
        path = pathOrURL;
      } else {
        path = path.replace(/\/$/, '') + '/' + pathOrURL;
      }
      pathOrURL = `${protocol}://${host}${port}${path}`;
    }
    return pathOrURL;
  }

  /**
   * Adds a slow request callback that will be called after any API request takes longer than the provided duration (ms)
   * @param minDuration
   * @param callback that accepts parameters: url, method, response, duration
   */
  addSlowRequestCallback(minDuration, callback) {
    this._slowRequestCallbacks.push({minDuration, callback});
  }

  setOnlineStatusWatcher() {
    const updateStatus = isOnline => {
      // Wrap in timeout with no delay, instead of using $scope.$apply()
      this.$timeout(() => {
        this.isDeviceOnline = isOnline;
      });
    };

    this.$window.addEventListener('online', () => updateStatus(true), false);
    this.$window.addEventListener('offline', () => updateStatus(false), false);
  }

  _notifyOnlineStatusListeners() {
    this._onlineStatusListeners.forEach(listener => listener(this.isOnline));
  }

  get isDeviceOnline() {
    return this._isDeviceOnline;
  }

  set isDeviceOnline(isDeviceOnline) {
    const previousOnlineStatus = this.isOnline;
    this._isDeviceOnline = isDeviceOnline;
    if (this.isOnline !== previousOnlineStatus) {
      // Notify listeners since effective status changed
      this._notifyOnlineStatusListeners();
    }
  }

  get isOnline() {
    return this.isDeviceOnline && !this.isOfflineMode;
  }

  set isOnline(isOnline) {
    this.isDeviceOnline = isOnline;
  }

  get isOfflineMode() {
    return this._isOfflineMode;
  }

  set isOfflineMode(isOfflineMode) {
    const previousOnlineStatus = this.isOnline;
    this._isOfflineMode = isOfflineMode;
    if (this.isOnline !== previousOnlineStatus) {
      // Notify listeners since effective status changed
      this._notifyOnlineStatusListeners();
    }
  }

  set cacheEnabled(enabled) {
    this.LogService.info("Setting APIService.cacheEnabled to", enabled);
    this._cacheEnabled = enabled;
  }

  get cacheEnabled() {
    return !!this._cacheEnabled;
  }

  get haveCredentials() {
    return !!this._haveCredentials;
  }

  /**
   * True while the onboarding capability is the credential. It travels only as a header, so
   * anything the browser fetches by itself, such as an <img src>, arrives without it.
   */
  get haveApplicationCredentials() {
    return !!this._haveApplicationCredentials;
  }

  addOnlineStatusListener(callback) {
    this._onlineStatusListeners.push(callback);
  }

  removeOnlineStatusListener(callback) {
    const idx = this._onlineStatusListeners.indexOf(callback);
    if (idx !== -1) {
      this._onlineStatusListeners.splice(idx, 1);
    }
  }

  getAllAndCreate(relativePath, factory, config = null) {
    return this.get(relativePath, config)
      .then(response => {
        if (!response || !response.data) {
          this.LogService.error(`getAllAndCreate(${relativePath}) response has no data`);
          return [];
        }
        const data = response.data;
        return data.map(objData => this.$q.when(factory(objData)))
      })
      .then(promises => this.$q.all(promises));
  }

  getById(basePath, id, factory) {
    let path = [basePath, id].join('/');
    return this.get(path)
      .then(data => factory(data));
  }

  /**
   * HTTP Get without basic auth - requires management user is logged in or call needs no authentication
   * @param relativePath string
   * @param config Configuration to pass to angular $http, with additional param 'force' to skip cache.
   *  Note that to pass URL parameters, config.params must be an object (matches $http.get API)
   * @returns promise
   */
  get(relativePath, config = null) {
    // Create a copy of config so that we don't mutate the passed object
    config = config || {};
    config = {...config};

    let params = config.params || null;
    delete config.params;

    let force = config.force || false;
    delete config.force;

    const url = this.convertPathToURL(relativePath, params);

    // Always check app cache unless force = true
    let checkCache = this.isCacheEnabledForConfig(config);
    if (checkCache && force) {
      if (this.isOnline) {
        checkCache = false;
      } else {
        this.LogService.warn(`APIService.get(${relativePath}) can't force load when application is offline`);
      }
    }

    let promise;
    if (checkCache) {
      promise = this.getCached(url)
        .catch(() => {
          this.LogService.info("Unable to retrieve cached version of:", url);
          return this.getAndCache(url, config);
        })
    } else {
      promise = this.getAndCache(url, config);
    }

    return promise;
  }

  /**
   * HTTP Get without basic auth - requires management user is logged in or call needs no authentication
   * @param relativePath string
   * @param data
   *  Note that to pass URL parameters, config.params must be an object (matches $http.get API)
   * @returns promise
   */
  postForNonApiUrl(relativePath, data) {
    return this.$http.post(relativePath, data)
      .catch(err => {
        this.LogService.error("Error posting to: ", relativePath);
        console.error(err);
      })
  }

  /**
   * HTTP Post without basic auth - requires management user is logged in or call needs no authentication
   * @param relativePath string
   * @param config Configuration to pass to angular $http, with additional param 'force' to skip cache.
   * @returns promise
   */
  getForNonApiUrl(relativePath, config = null) {
    // Create a copy of config so that we don't mutate the passed object
    config = config || {};
    config = {...config};

    const params = config.params || null;
    delete config.params;

    const force = config.force || false;
    delete config.force;

    const url = this.addURLParamsFromObject(relativePath, params);

    let promise;

    // Always check app cache unless force = true
    let checkCache = this.isCacheEnabledForConfig(config);
    if (checkCache && force) {
      if (this.isOnline) {
        checkCache = false;
      } else {
        this.LogService.warn(`APIService.get(${relativePath}) can't force load when application is offline`);
      }
    }

    if (checkCache) {
      promise = this.getCached(url)
        .catch(() => {
          this.LogService.info("Unable to retrieve cached version of:", url);
          return this.getAndCache(url, config);
        })
    } else {
      promise = this.getAndCache(url, config);
    }

    return promise;
  }

  getCached(url) {
    return this.ApplicationCache.getItem(url)
      .then(result => {
        if (result === null) {
          // Cache miss
          this.LogService.info("Cache miss: ", url);
          return this.$q.reject();
        } else if (isHtmlDocumentResponse(result)) {
          // A cache written before this guard can hold HTML; replaying it never recovers.
          this.LogService.warn("Discarding cached HTML response for: ", url);
          return this.ApplicationCache.removeItem(url)
            .catch(err => this.LogService.warn("Swallowing error evicting cached HTML response:", err))
            .then(() => this.$q.reject());
        } else {
          this.LogService.info("Cache hit: ", url);
          return result;
        }
      })
  }

  isCacheEnabledForConfig(config = null) {
    if (!this.cacheEnabled) {
      return false;
    }
    return !(config && config.disableCache);

  }

  /**
   * Previously, a request with config.disableCache=true would result in the cache not being updated
   * with the fresh result.
   * This changes the behavior such that the cache is always written as long as APIService.cacheEnabled
   * is true.
   *
   * @param config
   * @returns {boolean}
   */
  shouldUpdateCacheForConfig(config = null) {
    return this.cacheEnabled;

  }

  getAndCache(url, config = null) {
    return this._httpRequest('GET', url, config)
      .then(response => {
        if (response) {
          if (this.shouldUpdateCacheForConfig(config)) {
            const serialized = {
              data: response.data || null,
              status: response.status || null,
              statusText: response.statusText || null,
            };
            return this.ApplicationCache.setItem(url, serialized)
              .then(() => {
                return response;
              })
              .catch(err => {
                this.LogService.warn(`Swallowing error storing URL response for ${url} in app cache. Error:`, err, "Response:", response);
                return response;
              })
          } else {
            return response;
          }
        } else {
          this.LogService.error("Didn't receive response for URL", url);
          return response;
        }
      })
      .catch(err => {
        this.LogService.error("Error retrieving URL:", url, "error:", err);
        return this.$q.reject(err);
      })
  }

  _httpRequest(method, url, ...args) {
    const start = new Date();
    const $httpMethodName = method.toLowerCase();
    if (!($httpMethodName in this.$http)) {
      return this.$q.reject(createError({
        error: 'InvalidHTTPMethod',
        message: 'Invalid HTTP method',
        method,
        url,
      }))
    }

    let requestSizeInBytes = 0;
    let responseSizeInBytes = 0;
    let estimateDataSizeInBytes = data => {
      if (typeof data === 'string') {
        return data.length;
      } else {
        const encoded = angular.toJson(data);
        return encoded.length;
      }
    };
    if (args.length > 0) {
      requestSizeInBytes = estimateDataSizeInBytes(args[0]);
    }

    return this.$http[$httpMethodName](url, ...args)
      .catch(error => {
        const end = new Date();
        const duration = end - start;
        let responseSizeInBytes = 0;
        if (error.data) {
          responseSizeInBytes = estimateDataSizeInBytes(error.data);
        }
        this._processRequestCallbacks(method, url, null, duration, error.status, requestSizeInBytes, responseSizeInBytes);
        return this.$q.reject(error);
      })
      .then(response => {
        const end = new Date();
        const duration = end - start;
        let responseSizeInBytes = 0;
        if (response.data) {
          responseSizeInBytes = estimateDataSizeInBytes(response.data);
        }
        if (isHtmlDocumentResponse(response)) {
          // Null response as in .catch(); getAndCache() logs, so no log here.
          this._processRequestCallbacks(method, url, null, duration, response.status, requestSizeInBytes, responseSizeInBytes);
          return this.$q.reject(createError({
            error: 'UnexpectedHtmlResponse',
            // Keyed so it reaches the screen: an anonymous applicant on `apply.<host>`
            // has no console to read it from, and the reload is theirs to do.
            key: 'ERROR_SERVER_RETURNED_WEB_PAGE',
            message: 'The server returned a web page instead of data. Please reload the page.',
            method,
            url,
            status: response.status,
          }));
        }
        this._processRequestCallbacks(method, url, response, duration, response.status, requestSizeInBytes, responseSizeInBytes);
        return response;
      })
  }

  post(relativePath, ...args) {
    const url = this.convertPathToURL(relativePath);
    return this._httpRequest('POST', url, ...args);
  }

  put(relativePath, ...args) {
    const url = this.convertPathToURL(relativePath);
    return this._httpRequest('PUT', url, ...args);
  }

  delete(relativePath, ...args) {
    const url = this.convertPathToURL(relativePath);
    return this._httpRequest('DELETE', url, ...args);
  }

  _processRequestCallbacks(method, url, response, duration, status, bytesSent, bytesReceived) {
    this.LogService.debug(`APIService ${method} ${url} took ${duration} ms`);
    this._slowRequestCallbacks
      .filter(info => duration >= info.minDuration)
      .forEach(info => {
        info.callback(method, url, response, duration);
      });
    requestStatsCallbacks
      .forEach(callback => {
        callback(url, method, status, duration, bytesSent, bytesReceived);
      })
  }

  addURLParamsFromObject(path, paramObject) {
    return Object.keys(paramObject || {}).reduce((path, param) => {
      // Return URL with current param appended
      let sep = path.indexOf('?') === -1 ? '?' : '&';
      let val = paramObject[param] === undefined ? '' : encodeURIComponent(paramObject[param]);
      return `${path}${sep}${param}=${val}`;
    }, path);
  }

  convertPathToURL(path, params = null) {
    if (params) {
      path = this.addURLParamsFromObject(path, params);
    }
    if (path.match(/\/\//)) {
      return path;
    } else {
      return this._fullAPIURL + path;
    }
  }

  convertAbsolutePathToURL(absolutePath, params = null) {
    let path = absolutePath;
    if (params) {
      path = this.addURLParamsFromObject(path, params);
    }
    if (path.match(/\/\//)) {
      return path;
    } else {
      const fullAPIUrlWithoutPath = this._fullAPIURL.replace(/(.*\/\/[^\/]+)\/.*/, '$1');
      const absolutePathWithoutLeadingSlash = path.replace(/^\//, '');
      return [fullAPIUrlWithoutPath, absolutePathWithoutLeadingSlash].join('/');
    }
  }

  convertPathToURLWithHostPrefix(path, hostPrefix, params = null) {
    if (params) {
      path = this.addURLParamsFromObject(path, params);
    }
    let urlWithAPISubdomain;
    if (path.match(/\/\//)) {
      urlWithAPISubdomain = path;
    } else {
      urlWithAPISubdomain = this._fullAPIURLWithNoPath + path.replace(/^[\/]/, '');
    }

    // Now substitute the host prefix
    // NOTE: This assumes the full API URL always includes a host prefix, like "global" or "staff".
    return urlWithAPISubdomain.replace(/(\/\/)([^.\/]*)([.\/])/, `$1${hostPrefix}$3`);
  }

  convertAbsolutePathToURLWithHostPrefix(absolutePath, hostPrefix, params = null) {
    if (params) {
      absolutePath = this.addURLParamsFromObject(absolutePath, params);
    }
    let urlWithAPISubdomain;
    if (absolutePath.match(/\/\//)) {
      urlWithAPISubdomain = absolutePath;
    } else {
      const fullAPIUrlWithoutPath = this._fullAPIURL.replace(/(.*\/\/[^\/]+)\/.*/, '$1');
      const absolutePathWithoutLeadingSlash = absolutePath.replace(/^\//, '');
      urlWithAPISubdomain = [fullAPIUrlWithoutPath, absolutePathWithoutLeadingSlash].join('/');
    }

    // Now substitute the host prefix
    // NOTE: This assumes the full API URL always includes a host prefix, like "global" or "staff".
    return urlWithAPISubdomain.replace(/(\/\/)([^.\/]*)([.\/])/, `$1${hostPrefix}$3`);
  }

  setBasicAuthCredentials(username, password) {
    let authdata = this.Base64.encode(username + ':' + password);
    this.$http.defaults.headers.common['Authorization'] = 'Basic ' + authdata;
    this._haveCredentials = true;
    this._haveApplicationCredentials = false;
  }

  setAuthToken(token) {
    this.$http.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    this._authToken = token;
    this._haveCredentials = true;
    this._haveApplicationCredentials = false;
  }

  getAuthToken() {
    return this._authToken;
  }

  /**
   * Forgets the bearer token, not just the header: refreshTokenIfStale() reads
   * getAuthToken(), and a token left here after logout could be refreshed and
   * written back, signing the rep in again.
   */
  clearAuthToken() {
    delete this.$http.defaults.headers.common.Authorization;
    this._authToken = null;
  }

  setDataSyncUuidHeader(dataSyncUuid) {
    this.$http.defaults.headers.common['X-Data-Sync-UUID'] = dataSyncUuid;
  }

  removeDataSyncUuidHeader() {
    delete this.$http.defaults.headers.common['X-Data-Sync-UUID'];
  }

  setRecapExternalLinkCredentials(token) {
    this.setBasicAuthCredentials('', token);
  }

  /**
   * Sent the same way as a recap external link token, so it stays out of URLs and logs. Any
   * earlier bearer token is forgotten: thumbnail URLs carry getAuthToken() as ?token=, and a
   * token refresh would write its Bearer header back over the capability.
   */
  setApplicationCredentials(applicationId) {
    this.setBasicAuthCredentials('', applicationId);
    this._authToken = null;
    this._haveApplicationCredentials = true;
  }

  setCredentials(username, password) {
    return this.setBasicAuthCredentials(username, password);
  }

  clearCredentials() {
    delete this.$http.defaults.headers.common.Authorization;
    this._authorizationHeader = null;
    this._haveCredentials = false;
    this._haveApplicationCredentials = false;
  }

  /**
   * Add a new stats tracking callback.
   * For each external request, callback will be fired with arguments:
   *   uri
   *   method
   *   response_status_code
   *   duration
   *   bytes_sent
   *   bytes_received
   * @param callback
   */
  addStatsCallback(callback) {
    if (requestStatsCallbacks.indexOf(callback) === -1) {
      requestStatsCallbacks.push(callback);
    }
  }

  removeStatsCallback(callback) {
    const idx = requestStatsCallbacks.indexOf(callback);
    if (idx >= 0) {
      requestStatsCallbacks.splice(idx, 1);
    }
  }
}

export const moduleName = 'recaps.services.APIService';
export let module = angular.module(moduleName, [
  constants,
  appCache.name
]);
module.service('APIService', APIService);

module.factory('APIServiceHTTPInterceptor', ($q, $log) => {
  const addCustomRequestedWithHeader = false;
  let requestedWith = `${env.PACKAGE_NAME || 'unknown'} version ${env.FULL_VERSION || 'unknown'}`;
  return {
    'request': config => {
      if (addCustomRequestedWithHeader) {
        config.headers['X-Requested-With-App'] = requestedWith;
      }
      return config;
    },
    'response': response => {
      return response;
    },
    'responseError': response => {
      $log.warn("Error in response:", response);
      return $q.reject(response);
    },
  };
});

module.config(($httpProvider) => {
  $httpProvider.interceptors.push('APIServiceHTTPInterceptor');
});


module.factory('Base64', ($q, $log) => {
    const keyStr = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';

    return {
        encode: function (input) {
            let output = "";
            let chr1, chr2, chr3 = "";
            let enc1, enc2, enc3, enc4 = "";
            let i = 0;

            do {
                chr1 = input.charCodeAt(i++);
                chr2 = input.charCodeAt(i++);
                chr3 = input.charCodeAt(i++);

                enc1 = chr1 >> 2;
                enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
                enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
                enc4 = chr3 & 63;

                if (isNaN(chr2)) {
                    enc3 = enc4 = 64;
                } else if (isNaN(chr3)) {
                    enc4 = 64;
                }

                output = output +
                    keyStr.charAt(enc1) +
                    keyStr.charAt(enc2) +
                    keyStr.charAt(enc3) +
                    keyStr.charAt(enc4);
                chr1 = chr2 = chr3 = "";
                enc1 = enc2 = enc3 = enc4 = "";
            } while (i < input.length);

            return output;
        },

        decode: function (input) {
            let output = "";
            let chr1, chr2, chr3 = "";
            let enc1, enc2, enc3, enc4 = "";
            let i = 0;

            // remove all characters that are not A-Z, a-z, 0-9, +, /, or =
            let base64test = /[^A-Za-z0-9\+\/\=]/g;
            if (base64test.exec(input)) {
                window.alert("There were invalid base64 characters in the input text.\n" +
                    "Valid base64 characters are A-Z, a-z, 0-9, '+', '/',and '='\n" +
                    "Expect errors in decoding.");
            }
            input = input.replace(/[^A-Za-z0-9\+\/\=]/g, "");

            do {
                enc1 = keyStr.indexOf(input.charAt(i++));
                enc2 = keyStr.indexOf(input.charAt(i++));
                enc3 = keyStr.indexOf(input.charAt(i++));
                enc4 = keyStr.indexOf(input.charAt(i++));

                chr1 = (enc1 << 2) | (enc2 >> 4);
                chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
                chr3 = ((enc3 & 3) << 6) | enc4;

                output = output + String.fromCharCode(chr1);

                if (enc3 != 64) {
                    output = output + String.fromCharCode(chr2);
                }
                if (enc4 != 64) {
                    output = output + String.fromCharCode(chr3);
                }

                chr1 = chr2 = chr3 = "";
                enc1 = enc2 = enc3 = enc4 = "";

            } while (i < input.length);

            return output;
        }
    }
});

export default module;
